import React, { useState } from 'react';
import config from '../../config';

const AdminFolderPreview = ({ folder, onClose, onDownload }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  
  if (!folder) return null;

  const createThumbnailUrl = (photo) => {
    // Remove /api from config.apiUrl for uploads (uploads are served at root level)
    const baseUrl = config.apiUrl.replace('/api', '');
    return `${baseUrl}${photo.url}`;
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleDownloadClick = async () => {
    setIsDownloading(true);
    try {
      await onDownload(folder.session_id);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{folder.name}</h2>
              <p className="text-gray-600 mt-1">Session ID: {folder.session_id}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Folder Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-600">Created</p>
              <p className="font-semibold text-gray-800">
                {new Date(folder.created_at).toLocaleDateString()}
              </p>
            </div>
            {folder.order && (
              <>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">Print Size</p>
                  <p className="font-semibold text-gray-800">{folder.order.print_size || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600">Quantity</p>
                  <p className="font-semibold text-gray-800">{folder.order.quantity || 0}</p>
                </div>
              </>
            )}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-600">Total Photos</p>
              <p className="font-semibold text-gray-800">{folder.photos?.length || 0}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-4">
            <button
              onClick={handleDownloadClick}
              disabled={isDownloading}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isDownloading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Downloading...
                </>
              ) : (
                <>📥 Download ZIP</>
              )}
            </button>
          </div>
        </div>

        {/* Photos Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {folder.photos && folder.photos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {folder.photos.map((photo) => (
                <div
                  key={photo.id}
                  className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group"
                >
                  <img
                    src={createThumbnailUrl(photo)}
                    alt={photo.fileName}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity text-center text-white text-xs p-2">
                      <p className="font-semibold truncate">{photo.fileName}</p>
                      <p>{formatBytes(photo.size)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No photos in this folder
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Total: {folder.photos?.length || 0} photo(s)
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminFolderPreview;

